import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import { View, StyleSheet, useWindowDimensions } from "react-native";
import { useSetMessageFeedbackMutation, useGetLearningTtsTokenMutation } from "../../../graphql/generated/graphql";
import type { MessageItem } from "../../companion/companionMessage.types";
import type { ComposerAttachment } from "../../../components/chatNewInterface/chatLayout/messageComposer/ChatComposerBar";
import AITutorMessagesContainer from "./AITutorMessagesContainer";
import AITutorChatBarContainer from "../../navigation/AITutorChatBarContainer/AITutorChatBarContainer";
import CompanionErrorModal from "../../../components/companion/CompanionErrorModal/CompanionErrorModal";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import LottieView from "lottie-react-native";
import LottieUrl from "../../../assets/lottie/aiTutorChatLoadingLottie.json";
import { MathVirtualKeyboardProvider } from "../../../contexts/MathVirtualKeyboardContext";
import { shadowStyles } from "../../../styles/shadow";
import { radii, spacing, semanticColors } from "../../../styles/design-system";
import { ChatAudioPreferencesProvider, useChatAudioPreferences } from "../../../contexts/ChatAudioPreferencesContext";
import useLearningLlmStream from "../../../hooks/useLearningLlmStream";
import Sentry from "../../../utils/sentry";
import ObjectivesProgressBar from "../../../components/learning-activity/ObjectivesProgressBar/ObjectivesProgressBar";
import type { Objective } from "../../companion/LearningGoalsContainer";

const INTRO_TEXTS = [
  "Analyzing your progress and tailoring the conversation...",
  "The more you lean into learning, the faster you'll progress through your objectives.",
  "We detect when answers are copied from AI or other sources - please be original in your responses.",
  "Ask questions when you're curious! The AI loves when you dig deeper into topics.",
  "Don't worry about making mistakes - they're part of the learning process and help you grow.",
  "Take your time to think through each question. Quality of thought matters more than speed.",
  "Your previous conversations help the AI understand your learning style - each session gets more personalized.",
  "Try explaining concepts in your own words - it helps solidify your understanding.",
  "The AI remembers where you've struggled before and will help you strengthen those areas.",
  "Engage authentically - we can tell when responses aren't genuinely your own thinking.",
  "Every question is an opportunity to deepen your understanding of the subject.",
  "Your learning journey is unique - no two students will have the same experience here.",
  "Think critically about each concept before responding - deeper thinking leads to better learning.",
  "Feel free to ask for clarification if something doesn't make sense.",
  "The more engaged you are in the conversation, the more the AI can help you learn.",
  "Building on your past conversations to create the perfect learning experience for you.",
  "Quality responses help the AI understand your thought process and adapt accordingly.",
  "Each interaction helps build a more personalized and effective learning path.",
  "Curiosity and genuine engagement are the keys to accelerating your progress.",
  "Preparing an adaptive conversation based on your unique learning patterns...",
];

const getNextIntroText = (currentText?: string) => {
  if (INTRO_TEXTS.length <= 1) {
    return INTRO_TEXTS[0] ?? "";
  }
  const availableTexts = INTRO_TEXTS.filter((text) => text !== currentText);
  return availableTexts[Math.floor(Math.random() * availableTexts.length)] ?? INTRO_TEXTS[0];
};

type ChatDataMessage = {
  id?: string;
  content?: string;
  sender?: string;
  created?: string;
  feedback?: string;
  files?: Array<{ id: string; fileName: string }>;
  todos?: Array<{ id: string; name: string; date?: string | null }>;
  assignments?: Array<{ id: string; name?: string | null; due_date?: string | null }>;
};

type ChatData = {
  id?: string;
  conversation?: ChatDataMessage[];
};

interface AITutorChatContainerProps {
  chatData: ChatData | undefined;
  chatLoading: boolean;
  resourceId: string;
  isScreenFocused?: boolean;
  objectives?: Objective[];
  onObjectiveCompleted?: (objectiveIds: string[]) => void;
  onAllObjectivesCompleted?: () => void;
  onStreamingStateChange?: (isStreaming: boolean) => void;
  onStreamCompleted?: () => void;
}

function AITutorChatContent({
  chatData,
  chatLoading,
  resourceId,
  isScreenFocused = true,
  objectives,
  onObjectiveCompleted,
  onAllObjectivesCompleted,
  onStreamingStateChange,
  onStreamCompleted,
}: AITutorChatContainerProps) {
  const { isAudioOn, playbackSpeed } = useChatAudioPreferences();
  const { startStream, isStreaming } = useLearningLlmStream();

  const [messages, setMessages] = useState<MessageItem[]>([]);
  const [currentStreamingMessageId, setCurrentStreamingMessageId] = useState<string | null>(null);
  const [resetAutoScroll, setResetAutoScroll] = useState(0);
  const streamingMessageIdRef = useRef<string | null>(null);
  const lastCompletedStreamingMessageIdRef = useRef<string | null>(null);
  const hasStreamedLocallyRef = useRef(false);
  const activeChatIdRef = useRef<string | null>(chatData?.id ?? null);

  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorRetry, setErrorRetry] = useState<(() => void) | undefined>(undefined);
  const [feedbackByMessageId, setFeedbackByMessageId] = useState<Record<string, "up" | "down">>({});
  const [setMessageFeedbackMutation] = useSetMessageFeedbackMutation();
  const [getLearningTtsToken] = useGetLearningTtsTokenMutation();
  const [isFirstAiStreamInProgress, setIsFirstAiStreamInProgress] = useState(false);
  const [shouldRevealFirstMessageAtTop, setShouldRevealFirstMessageAtTop] = useState(false);
  const [autoReadAloudMessageId, setAutoReadAloudMessageId] = useState<string | null>(null);
  const [introText, setIntroText] = useState(() => INTRO_TEXTS[0] ?? "");
  const isFirstAiStreamInProgressRef = useRef(false);
  const hasCompletedFirstAiStreamRef = useRef(false);
  const pendingAutoReadAloudMessageIdRef = useRef<string | null>(null);
  const { width } = useWindowDimensions();

  const getTtsToken = useCallback(async () => {
    const result = await getLearningTtsToken();
    return result.data?.getLearningTtsToken?.token ?? null;
  }, [getLearningTtsToken]);

  const messageReadAloudTts = useMemo(
    () => ({
      resetPlayback: () => undefined,
      suspendPlayback: () => undefined,
      resumePlayback: () => undefined,
      getTtsToken,
    }),
    [getTtsToken]
  );

  const shouldShowIntroLoader = !chatData?.id && isFirstAiStreamInProgress && currentStreamingMessageId !== null;

  const initialMessage = useMemo(
    () => (chatData?.id ? "Let's continue where we left off" : "Please begin by introducing me to this topic"),
    [chatData?.id]
  );

  useEffect(() => {
    hasStreamedLocallyRef.current = false;
    streamingMessageIdRef.current = null;
    lastCompletedStreamingMessageIdRef.current = null;
    isFirstAiStreamInProgressRef.current = false;
    hasCompletedFirstAiStreamRef.current = false;
    pendingAutoReadAloudMessageIdRef.current = null;
    activeChatIdRef.current = null;
    setAutoReadAloudMessageId(null);
    setCurrentStreamingMessageId(null);
    setIsFirstAiStreamInProgress(false);
    setShouldRevealFirstMessageAtTop(false);
    setMessages([]);
    setIntroText(INTRO_TEXTS[0] ?? "");
  }, [resourceId]);

  useEffect(() => {
    if (chatData?.id) {
      hasCompletedFirstAiStreamRef.current = true;
      isFirstAiStreamInProgressRef.current = false;
      setIsFirstAiStreamInProgress(false);
      activeChatIdRef.current = chatData.id;
    }
  }, [chatData?.id]);

  useEffect(() => {
    onStreamingStateChange?.(currentStreamingMessageId !== null);
  }, [currentStreamingMessageId, onStreamingStateChange]);

  useEffect(() => {
    if (!shouldShowIntroLoader) {
      return;
    }
    setIntroText((prev) => getNextIntroText(prev));
    const intervalId = setInterval(() => {
      setIntroText((prev) => getNextIntroText(prev));
    }, 2500);
    return () => {
      clearInterval(intervalId);
    };
  }, [shouldShowIntroLoader]);

  useEffect(() => {
    if (!chatData?.conversation || hasStreamedLocallyRef.current) {
      return;
    }

    const serverMessages: MessageItem[] = [...chatData.conversation].reverse().map((message, index) => ({
      id: message.id ?? `server-message-${index}`,
      sender: message.sender ?? "ai",
      content: message.content ?? "",
      created: message.created,
      files: message.files?.length ? message.files.map((f) => ({ id: f.id, fileName: f.fileName })) : undefined,
      todos: message.todos?.length ? message.todos : undefined,
      assignments: message.assignments?.length
        ? message.assignments.map((a) => ({ id: a.id, name: a.name ?? undefined, due_date: a.due_date ?? undefined }))
        : undefined,
    }));

    setMessages(serverMessages);

    const initialFeedback: Record<string, "up" | "down"> = {};
    for (const message of chatData.conversation) {
      const fb = message.feedback;
      if (fb === "up" || fb === "down") {
        initialFeedback[message.id ?? ""] = fb;
      }
    }
    setFeedbackByMessageId(initialFeedback);
  }, [chatData]);

  const handleStreamStart = useCallback(
    (message: string, attachment?: ComposerAttachment | null, meta?: { isAutoStart?: boolean }) => {
      hasStreamedLocallyRef.current = true;
      const isFirstAiStream = Boolean(meta?.isAutoStart) && !chatData?.id && !hasCompletedFirstAiStreamRef.current;
      if (isFirstAiStream) {
        isFirstAiStreamInProgressRef.current = true;
        setIsFirstAiStreamInProgress(true);
        setShouldRevealFirstMessageAtTop(false);
      } else {
        isFirstAiStreamInProgressRef.current = false;
        setIsFirstAiStreamInProgress(false);
        hasCompletedFirstAiStreamRef.current = true;
      }
      pendingAutoReadAloudMessageIdRef.current = null;
      setAutoReadAloudMessageId(null);
      setResetAutoScroll((prev) => prev + 1);
      const userMessageId = `user-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const aiTempId = `ai-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      streamingMessageIdRef.current = aiTempId;
      lastCompletedStreamingMessageIdRef.current = aiTempId;
      setCurrentStreamingMessageId(aiTempId);
      const files = attachment ? [{ id: attachment.id, fileName: attachment.name }] : undefined;

      setMessages((prev) => {
        const nextMessages = [...prev];
        if (!meta?.isAutoStart) {
          nextMessages.push({ id: userMessageId, sender: "human", content: message, files });
        }
        nextMessages.push({ id: aiTempId, sender: "ai", content: "" });
        return nextMessages;
      });
    },
    [chatData?.id]
  );

  const handleStreamChunk = useCallback(({ text, isFinalChunk }: { text: string; isFinalChunk?: boolean }) => {
    const streamingId = streamingMessageIdRef.current;
    if (!streamingId) {
      return;
    }
    if (text) {
      setMessages((prev) =>
        prev.map((message) =>
          message.id === streamingId ? { ...message, content: `${message.content ?? ""}${text}` } : message
        )
      );
    }
    if (isFinalChunk) {
      lastCompletedStreamingMessageIdRef.current = streamingId;
      streamingMessageIdRef.current = null;
      setCurrentStreamingMessageId(null);
    }
  }, []);

  const handleStreamComplete = useCallback(
    (chatId?: string) => {
      const completedMessageId = streamingMessageIdRef.current ?? lastCompletedStreamingMessageIdRef.current;
      if (isFirstAiStreamInProgressRef.current) {
        hasCompletedFirstAiStreamRef.current = true;
        isFirstAiStreamInProgressRef.current = false;
        setIsFirstAiStreamInProgress(false);
        setShouldRevealFirstMessageAtTop(true);
      }
      streamingMessageIdRef.current = null;
      setCurrentStreamingMessageId(null);
      if (chatId) {
        activeChatIdRef.current = chatId;
      }
      if (completedMessageId && isAudioOn) {
        pendingAutoReadAloudMessageIdRef.current = completedMessageId;
        setAutoReadAloudMessageId(completedMessageId);
      }
      onStreamCompleted?.();
    },
    [isAudioOn, onStreamCompleted]
  );

  const handleRevealedFirstMessageAtTop = useCallback(() => {
    setShouldRevealFirstMessageAtTop(false);
  }, []);

  const handleStreamError = useCallback(
    ({ title, message, onRetry }: { title: string; message: string; onRetry?: () => void }) => {
      const streamingId = streamingMessageIdRef.current;
      if (streamingId) {
        setMessages((prev) => prev.filter((item) => item.id !== streamingId));
      }
      if (isFirstAiStreamInProgressRef.current) {
        isFirstAiStreamInProgressRef.current = false;
        setIsFirstAiStreamInProgress(false);
        setShouldRevealFirstMessageAtTop(false);
      }
      streamingMessageIdRef.current = null;
      setCurrentStreamingMessageId(null);
      setErrorTitle(title);
      setErrorMessage(message);
      setErrorRetry(() => onRetry);
      setErrorModalVisible(true);
      pendingAutoReadAloudMessageIdRef.current = null;
      setAutoReadAloudMessageId(null);
    },
    []
  );

  const handleFeedback = useCallback(
    async (messageId: string, value: "up" | "down" | null) => {
      setFeedbackByMessageId((prev) => {
        if (value === null) {
          const next = { ...prev };
          delete next[messageId];
          return next;
        }
        return { ...prev, [messageId]: value };
      });
      datadogRum.addAction("message_feedback", { messageId, chatType: "ile", value });
      try {
        await setMessageFeedbackMutation({ variables: { messageId, value: value ?? "" } });
      } catch {
        setErrorTitle("Feedback Error");
        setErrorMessage("Unable to submit feedback. Please try again.");
        setErrorModalVisible(true);
      }
    },
    [setMessageFeedbackMutation]
  );

  const sendMessage = useCallback(
    async (text: string) => {
      if (isStreaming) return;
      handleStreamStart(text);
      try {
        await startStream(text, {
          chatId: activeChatIdRef.current ?? undefined,
          contentNodeId: resourceId,
          onChunk: ({ text: chunk, isFinalChunk }) => {
            handleStreamChunk({ text: chunk, isFinalChunk });
          },
          onChatId: (nextChatId) => {
            activeChatIdRef.current = nextChatId;
          },
          onObjectiveCompleted: ({ objectiveIds }) => {
            onObjectiveCompleted?.(objectiveIds);
          },
          onAllObjectivesCompleted: () => {
            onAllObjectivesCompleted?.();
          },
          onComplete: ({ event }) => {
            handleStreamComplete(event?.chatId);
          },
          onError: ({ error }) => {
            Sentry.captureException(error);
            handleStreamError({
              title: "Failed to send message",
              message: error.message || "An error occurred. Please try again.",
            });
          },
        });
      } catch (error) {
        Sentry.captureException(error);
        handleStreamError({
          title: "Failed to send message",
          message:
            error instanceof Error
              ? error.message || "An error occurred. Please try again."
              : "An error occurred. Please try again.",
        });
      }
    },
    [
      isStreaming,
      resourceId,
      startStream,
      handleStreamStart,
      handleStreamChunk,
      onObjectiveCompleted,
      onAllObjectivesCompleted,
      handleStreamComplete,
      handleStreamError,
    ]
  );

  return (
    <MathVirtualKeyboardProvider>
      <View style={styles.container}>
        <View style={styles.content}>
          <View style={styles.messagesArea}>
            {shouldShowIntroLoader ? (
              <View style={styles.loaderWrapper}>
                <View style={[styles.loaderContainer, shadowStyles.allAroundShadow]}>
                  <LottieView autoPlay source={LottieUrl} speed={1} loop={true} style={{ width: 100, height: 100 }} />
                  <CustomText
                    text="Loading your personalized learning experience"
                    useTranslationText={false}
                    size="l"
                    weight="bold"
                    style={{ textAlign: "center" }}
                    aria-level="2"
                    role="heading"
                  />
                  <CustomText
                    text={introText}
                    useTranslationText={false}
                    size="m"
                    weight="regular"
                    style={{ marginTop: 10, textAlign: "center" }}
                  />
                </View>
              </View>
            ) : (
              <AITutorMessagesContainer
                resourceId={resourceId}
                messages={messages}
                isStreaming={currentStreamingMessageId !== null}
                currentStreamingMessageId={currentStreamingMessageId}
                resetAutoScroll={resetAutoScroll}
                shouldRevealFirstMessageAtTop={shouldRevealFirstMessageAtTop}
                onRevealedFirstMessageAtTop={handleRevealedFirstMessageAtTop}
                feedbackByMessageId={feedbackByMessageId}
                onFeedback={handleFeedback}
                messageReadAloudTts={messageReadAloudTts}
                autoReadAloudEnabled={isAudioOn}
                autoReadAloudMessageId={autoReadAloudMessageId}
                playbackSpeed={playbackSpeed}
                isScreenFocused={isScreenFocused}
              />
            )}
            <View style={[styles.chatInputWrapper, width < 850 ? styles.chatInputWrapperPadding : {}]}>
              {objectives && objectives.length > 0 && (
                <ObjectivesProgressBar
                  objectives={objectives}
                  // sendMessage and AITutorChatBarContainer share the same isStreaming lock from
                  // useLearningLlmStream — only one stream can be in-flight at a time.
                  isStreaming={isStreaming}
                  onMoveOn={() => sendMessage("I'm ready to move on to the next objective")}
                />
              )}
              <AITutorChatBarContainer
                key={resourceId}
                chatId={chatData?.id ?? null}
                contentNodeId={resourceId}
                startStream={startStream}
                isStreaming={isStreaming}
                autoStart={!chatLoading && !chatData?.id}
                initialMessage={initialMessage}
                onStreamStart={handleStreamStart}
                onStreamChunk={handleStreamChunk}
                onObjectiveCompleted={onObjectiveCompleted}
                onAllObjectivesCompleted={onAllObjectivesCompleted}
                onStreamComplete={handleStreamComplete}
                onStreamError={handleStreamError}
              />
              <CustomText
                useTranslationText
                size="xs"
                weight="regular"
                text="ai_tutor_chat_disclaimer"
                style={styles.disclaimerText}
              />
            </View>
          </View>
        </View>
        <CompanionErrorModal
          visible={errorModalVisible}
          title={errorTitle}
          message={errorMessage}
          onRetry={errorRetry}
          onClose={() => {
            setErrorModalVisible(false);
            setErrorRetry(undefined);
          }}
        />
      </View>
    </MathVirtualKeyboardProvider>
  );
}

function AITutorChatContainer(props: AITutorChatContainerProps) {
  return (
    <ChatAudioPreferencesProvider>
      <AITutorChatContent {...props} />
    </ChatAudioPreferencesProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: semanticColors.surface.white,
  },
  content: {
    flex: 1,
    position: "relative",
  },
  messagesArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    backgroundColor: semanticColors.surface.light,
  },
  chatInputWrapper: {
    width: "100%",
    maxWidth: 1152,
    alignSelf: "center",
    zIndex: 900,
  },
  chatInputWrapperPadding: {
    paddingLeft: 80,
    paddingRight: 24,
  },
  disclaimerText: {
    textAlign: "center",
    paddingBottom: spacing.xs,
    color: semanticColors.typography.textLight,
  },
  loaderWrapper: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loaderContainer: {
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: semanticColors.surface.white,
    borderRadius: radii.lg,
    borderWidth: 0,
    maxWidth: 500,
    width: "90%",
    padding: spacing["2xl"],
  },
});

export default AITutorChatContainer;
